// src/data/prompts-compiler.js
import negativeKeywordIdentificationPrompt from "./prompt-templates/negative-keyword-identification.js";
import keywordExpansionOpportunitiesPrompt from "./prompt-templates/keyword-expansion-opportunities.js";
import budgetOptimizationRecommendationsPrompt from "./prompt-templates/budget-optimization-recommendations.js";
import searchTermPatternAnalysisPrompt from "./prompt-templates/search-term-pattern-analysis.js";
import pmaxPlacementPerformancePrompt from "./prompt-templates/pmax-placement-performance.js";
import pmaxChannelDistributionAnalysisPrompt from "./prompt-templates/pmax-channel-distribution-analysis.js";
import performanceTrendAnalysisPrompt from "./prompt-templates/performance-trend-analysis.js";
import roasMaximizationStrategyPrompt from "./prompt-templates/roas-maximization-strategy.js";
import qualityScoreImprovementPlanPrompt from "./prompt-templates/quality-score-improvement-plan.js";
import shoppingProductPerformanceAnalysisPrompt from "./prompt-templates/shopping-product-performance-analysis.js";
import conversionActionPerformancePrompt from "./prompt-templates/conversion-action-performance.js";
import campaignSettingsAuditPrompt from "./prompt-templates/campaign-settings-audit.js";
import dayHourPerformancePatternsPrompt from "./prompt-templates/day-hour-performance-patterns.js";
import performancePeriodComparisonPrompt from "./prompt-templates/performance-period-comparison.js";
import audiencePerformanceComparisonPrompt from "./prompt-templates/audience-performance-comparison.js";
import audienceExpansionOpportunitiesPrompt from "./prompt-templates/audience-expansion-opportunities.js";
import devicePerformanceOptimizationPrompt from "./prompt-templates/device-performance-optimization.js";
import mobileExperienceAuditPrompt from "./prompt-templates/mobile-experience-audit.js";
import geographicPerformanceAnalysisPrompt from "./prompt-templates/geographic-performance-analysis.js";
import locationExpansionOpportunitiesPrompt from "./prompt-templates/location-expansion-opportunities.js";
import adAssetPerformanceAnalysisPrompt from "./prompt-templates/ad-asset-performance-analysis.js";
import messageTestingStrategyPrompt from "./prompt-templates/message-testing-strategy.js";
import keywordNgramAnalysisPrompt from "./prompt-templates/keyword-ngram-analysis.js";
import searchTermNgramAnalysisPrompt from "./prompt-templates/search-term-ngram-analysis.js";
import smartBiddingAnalysisPrompt from "./prompt-templates/smart-bidding-analysis.js";
import campaignScalingAnalysisPrompt from "./prompt-templates/campaign-scaling-analysis.js";
import adCopyImprovementPrompt from "./prompt-templates/ad-copy-improvement.js";

const systemPrompt =
  "You are a Google Ads expert. Provide clear, actionable, and concise recommendations or analysis based on the user's request and the provided data. Focus on best practices, performance improvement, and practical next steps. If the user provides campaign data, use it to support your advice.";

export default {
  system: systemPrompt,
  prompts: [
    {
      id: "10061",
      category: "Bidding",
      title: "Smart Bidding Analysis",
      description:
        "Comprehensive analysis of campaign readiness for Smart Bidding strategies with target recommendations based on performance data and business goals.",
      prompt: smartBiddingAnalysisPrompt,
      rating: 4.9,
      dataSource: "smartBiddingAnalysisV1",
      views: 304,
      author: "Aaron Young",
      lastUpdated: "Today",
      memberOnly: true,
      comingSoon: false,
      featured: true,
    },
    {
      id: "10062",
      category: "Bidding",
      title: "Campaign Scaling Readiness",
      description:
        "Identify which campaigns are ready for a 20% budget increase based on Aaron Young's scaling framework. Analyzes profitability consistency, budget change impact, and impression share constraints.",
      prompt: campaignScalingAnalysisPrompt,
      rating: 4.8,
      dataSource: "campaignScalingAnalysisV1",
      views: 0,
      author: "Aaron Young",
      lastUpdated: "Today",
      memberOnly: true,
      comingSoon: false,
      featured: true,
    },
    {
      id: "10063",
      category: "Creative",
      title: "Ad Copy Improvement",
      description:
        "Get specific headline and description rewrites for underperforming RSAs (CTR below 7%) using Aaron Young's 4-point copywriting framework: Keyword Focus, Brand Mention, Call to Action, and Emotional Trigger.",
      prompt: adCopyImprovementPrompt,
      rating: 4.8,
      dataSource: "adCopyAnalysisV1",
      views: 0,
      author: "Aaron Young",
      lastUpdated: "Today",
      memberOnly: true,
      comingSoon: false,
      featured: true,
    },
    {
      id: "10001",
      category: "Search Terms",
      title: "Negative Keyword Identification",
      description:
        "Find potential negative keywords based on high-cost, non-converting search queries.",
      prompt: negativeKeywordIdentificationPrompt,
      rating: 5.0,
      dataSource: "search80",
      views: 500,
      author: "Mike Rhodes",
      lastUpdated: "Yesterday",
      memberOnly: false,
    },
    {
      id: "10002",
      category: "Keywords",
      title: "Keyword Expansion Opportunities",
      description:
        "Discover high-converting search terms that aren't currently keywords.",
      prompt: keywordExpansionOpportunitiesPrompt,
      rating: 5.0,
      dataSource: "keyword80",
      views: 425,
      author: "Mike Rhodes",
      lastUpdated: "Today",
      memberOnly: true,
    },
    {
      id: "10003",
      category: "Campaigns",
      title: "Campaign Settings Audit",
      description:
        "Identify inconsistencies or optimization opportunities in campaign settings.",
      prompt: campaignSettingsAuditPrompt,
      rating: 4.4,
      dataSource: "campaignSettingsAuditDataV1",
      views: 270,
      author: "Mike Rhodes",
      lastUpdated: "May 18th, 2025",
      memberOnly: false,
    },
    {
      id: "10004",
      category: "PMax",
      title: "PMax Placement Performance",
      description:
        "Analyze Performance Max placement data to identify low-quality sites and apps.",
      prompt: pmaxPlacementPerformancePrompt,
      rating: 4.6,
      dataSource: "placements",
      views: 320,
      author: "Mike Rhodes",
      lastUpdated: "May 16th, 2025",
      memberOnly: false,
    },
    {
      id: "10005",
      category: "Campaigns",
      title: "ROAS Maximization Strategy",
      description:
        "Develops a bidding strategy to maximize Return on Ad Spend (ROAS) based on detailed campaign performance analysis, including optimal spend zones and ROAS at different spend levels.",
      prompt: roasMaximizationStrategyPrompt,
      dataSource: "campaignProfitAnalysisV1",
      memberOnly: false,
      author: "Mike Rhodes",
      lastUpdated: "Yesterday",
      rating: 4.7,
      reviewCount: 92,
    },
    {
      id: "10006",
      category: "Bidding",
      title: "Budget Optimization Recommendations",
      description:
        "Provides recommendations for optimizing campaign budgets based on performance data and potential for increased profitability or efficiency.",
      prompt: budgetOptimizationRecommendationsPrompt,
      dataSource: "campaignProfitAnalysisV1",
      memberOnly: true,
      author: "Mike Rhodes",
      lastUpdated: "Today",
      rating: 4.8,
      reviewCount: 115,
    },
    {
      id: "10007",
      category: "Trends",
      title: "Performance Trend Analysis",
      description:
        "Identify significant campaign performance trends over time.",
      prompt: performanceTrendAnalysisPrompt,
      rating: 4.7,
      dataSource: "daily",
      views: 380,
      author: "Mike Rhodes",
      lastUpdated: "May 18th, 2025",
      memberOnly: true,
    },
    {
      id: "10008",
      category: "Keywords",
      title: "Keyword N-gram Analysis (1-word)",
      description:
        "Analyzes 1-word n-gram patterns from keyword data for actionable insights on bidding and keyword strategy.",
      prompt: keywordNgramAnalysisPrompt,
      dataSource: "keywordNgramAnalysisV1",
      memberOnly: false,
      author: "Mike Rhodes",
      rating: 4.7,
      views: 0,
      lastUpdated: "May 16th, 2025",
    },
    {
      id: "10009",
      category: "Search Terms",
      title: "Search Term N-gram Analysis (1-word)",
      description:
        "Analyzes 1-word n-gram patterns from search term data for insights on user intent, negative keywords, and new keyword opportunities.",
      prompt: searchTermNgramAnalysisPrompt,
      dataSource: "searchTermNgramAnalysisV1",
      memberOnly: true,
      author: "Mike Rhodes",
      rating: 4.7,
      views: 0,
      lastUpdated: "Yesterday",
    },
    {
      id: "10010",
      category: "Shopping",
      title: "Shopping Product Performance Analysis",
      description:
        "Identify top and underperforming products in Shopping campaigns.",
      prompt: shoppingProductPerformanceAnalysisPrompt,
      rating: 4.5,
      dataSource: "products",
      views: 320,
      author: "Mike Rhodes",
      lastUpdated: "Today",
      memberOnly: true,
    },
    {
      id: "10011",
      category: "Conversions",
      title: "Conversion Action Performance",
      description:
        "Analyze conversion actions to identify the most valuable user behaviors.",
      prompt: conversionActionPerformancePrompt,
      rating: 4.7,
      dataSource: "conv_action",
      views: 290,
      author: "Mike Rhodes",
      lastUpdated: "May 18th, 2025",
      memberOnly: true,
    },
    {
      id: "10013",
      category: "Trends",
      title: "Day & Hour Performance Patterns",
      description: "Identify optimal days and hours for campaign performance.",
      prompt: dayHourPerformancePatternsPrompt,
      rating: 4.6,
      dataSource: "h_dow_30d",
      views: 310,
      author: "Mike Rhodes",
      lastUpdated: "May 16th, 2025",
      memberOnly: true,
    },
    {
      id: "10014",
      category: "Campaigns",
      title: "Performance Period Comparison",
      description:
        "Compare current period performance to previous period to identify trends.",
      prompt: performancePeriodComparisonPrompt,
      rating: 4.5,
      dataSource: "thirty_days",
      views: 300,
      author: "Mike Rhodes",
      lastUpdated: "Yesterday",
      memberOnly: true,
      comingSoon: true,
    },
    {
      id: "10015",
      category: "Keywords",
      title: "Quality Score Improvement Plan",
      description:
        "Identify low quality score keywords and get recommendations for improvement.",
      prompt: qualityScoreImprovementPlanPrompt,
      rating: 4.6,
      dataSource: "keyword80",
      views: 350,
      author: "Mike Rhodes",
      lastUpdated: "Today",
      memberOnly: true,
      comingSoon: true,
    },
    {
      id: "10030",
      category: "Search Terms",
      title: "Search Term Pattern Analysis",
      description:
        "Identify high-performing word patterns and modifiers in search queries.",
      prompt: searchTermPatternAnalysisPrompt,
      rating: 4.7,
      dataSource: "search80",
      views: 380,
      author: "Mike Rhodes",
      lastUpdated: "May 18th, 2025",
      memberOnly: true,
    },
    {
      id: "10035",
      category: "PMax",
      title: "PMax Channel Distribution Analysis",
      description:
        "Analyze how your Performance Max campaigns distribute spend across different channels.",
      prompt: pmaxChannelDistributionAnalysisPrompt,
      rating: 5.0,
      dataSource: "pmax_perf",
      views: 290,
      author: "Mike Rhodes",
      lastUpdated: "May 16th, 2025",
      memberOnly: false,
    },
    {
      id: "10050",
      category: "Audiences",
      title: "Audience Performance Comparison",
      description:
        "Compare performance across different audience segments to optimize targeting.",
      prompt: audiencePerformanceComparisonPrompt,
      rating: 4.7,
      dataSource: "thirty_days",
      views: 0,
      author: "Mike Rhodes",
      lastUpdated: "Yesterday",
      memberOnly: true,
      comingSoon: true,
    },
    {
      id: "10051",
      category: "Audiences",
      title: "Audience Expansion Opportunities",
      description:
        "Discover new audience segments to target based on current performance.",
      prompt: audienceExpansionOpportunitiesPrompt,
      rating: 4.5,
      dataSource: "thirty_days",
      views: 0,
      author: "Mike Rhodes",
      lastUpdated: "Today",
      memberOnly: true,
      comingSoon: true,
    },
    {
      id: "10052",
      category: "Devices",
      title: "Device Performance Optimization",
      description:
        "Analyze performance by device type and recommend bid adjustments.",
      prompt: devicePerformanceOptimizationPrompt,
      rating: 4.6,
      dataSource: "thirty_days",
      views: 0,
      author: "Mike Rhodes",
      lastUpdated: "May 18th, 2025",
      memberOnly: true,
      comingSoon: true,
    },
    {
      id: "10053",
      category: "Devices",
      title: "Mobile Experience Audit",
      description:
        "Identify mobile-specific issues and opportunities in your campaigns.",
      prompt: mobileExperienceAuditPrompt,
      rating: 4.4,
      dataSource: "thirty_days",
      views: 0,
      author: "Mike Rhodes",
      lastUpdated: "May 16th, 2025",
      memberOnly: true,
      comingSoon: true,
    },
    {
      id: "10054",
      category: "Locations",
      title: "Geographic Performance Analysis",
      description:
        "Identify high and low-performing locations to optimize geographic targeting.",
      prompt: geographicPerformanceAnalysisPrompt,
      rating: 4.8,
      dataSource: "thirty_days",
      views: 0,
      author: "Mike Rhodes",
      lastUpdated: "Yesterday",
      memberOnly: true,
      comingSoon: true,
    },
    {
      id: "10055",
      category: "Locations",
      title: "Location Expansion Opportunities",
      description:
        "Discover promising new locations to target based on current performance.",
      prompt: locationExpansionOpportunitiesPrompt,
      rating: 4.5,
      dataSource: "thirty_days",
      views: 0,
      author: "Mike Rhodes",
      lastUpdated: "Today",
      memberOnly: true,
      comingSoon: true,
    },
    {
      id: "10056",
      category: "Creative",
      title: "Ad Asset Performance Analysis",
      description:
        "Identify your best and worst performing ad components across campaigns.",
      prompt: adAssetPerformanceAnalysisPrompt,
      rating: 4.7,
      dataSource: "assets",
      views: 0,
      author: "Mike Rhodes",
      lastUpdated: "May 18th, 2025",
      memberOnly: true,
      comingSoon: true,
    },
    {
      id: "10057",
      category: "Creative",
      title: "Message Testing Strategy",
      description:
        "Develop a structured testing plan for ad messaging based on current performance.",
      prompt: messageTestingStrategyPrompt,
      rating: 4.6,
      dataSource: "thirty_days",
      views: 0,
      author: "Mike Rhodes",
      lastUpdated: "May 16th, 2025",
      memberOnly: true,
      comingSoon: true,
    },
  ],
};
